import React, { useState } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import MemoChevron from '../../icons/Chevron';
import MemoCheckboxIcon from '../../icons/CheckboxIcon';
import MemoPlusIcon from '../../icons/PlusIcon';
import MemoSearchIcon from '../../icons/SearchIcon';
import CloseIcon from '../../icons/CloseIcon';

interface RefinementListWithSearchProps {
  attribute: string;
  title: string;
  hasSearchBox?: boolean;
  isDateFilter?: boolean;
  alwaysOpen?: boolean;
  onRefine?: (value: string, isRefined: boolean) => void;
}

const RefinementListWithSearch: React.FC<RefinementListWithSearchProps> = (
  props
) => {
  const { title, hasSearchBox, isDateFilter, alwaysOpen, attribute, onRefine } =
    props;
  const {
    items,
    refine,
    searchForItems,
    canToggleShowMore,
    isShowingMore,
    toggleShowMore,
  } = useRefinementList({
    attribute,
    showMore: true,
    limit: 5,
    showMoreLimit: 60,
    operator: 'and',
    sortBy: ['count:desc', 'name:asc'],
  });

  const { refresh } = useInstantSearch();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const filteredItems = items.filter((item) => {
    if (!query) {
      return true;
    }

    const label = item.label ?? '';
    return label.toLowerCase().includes(query.toLowerCase());
  });

  const wrapperClasses = `case-notes__refinement ${
    open || alwaysOpen ? 'case-notes__refinement--open' : ''
  } ${
    !isDateFilter && !query && filteredItems.length === 0 ? 'is-hidden' : ''
  }`;

  const handleDateChange = () => {
    // apply new refinements if dates are set
    let toDateValue = toDate;
    let fromDateValue = fromDate;

    // if fromDateValue is just a year (i.e 2023) prepend it with 01/
    if (fromDateValue && fromDateValue.length === 4) {
      fromDateValue = `01/${fromDateValue}`;
    }

    // if toDateValue is just a year (i.e 2023) prepend it with 12/
    if (toDateValue && toDateValue.length === 4) {
      toDateValue = `12/${toDateValue}`;
    }

    // validate the date format matches mm/yyyy
    const dateRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;

    if (!dateRegex.test(fromDateValue)) {
      fromDateValue = '';
    } else {
      // convert it from mm/yyyy to YYYY-mm-01
      const [month, year] = fromDateValue.split('/');

      fromDateValue = `${year}-${month}-01`;
    }

    if (!dateRegex.test(toDateValue)) {
      toDateValue = '';
    } else {
      // convert it from mm/yyyy to YYYY-mm-01
      const [month, year] = toDateValue.split('/');

      let nextMonth = parseInt(month, 10);
      let nextYear = parseInt(year, 10);

      // if the month is 12 then set the next month to 01 and increment the year
      if (nextMonth > 11) {
        nextMonth = 1;
        nextYear += 1;
      }

      // make sure the month is 2 digits
      const nextMonthStr = nextMonth < 10 ? `0${nextMonth}` : nextMonth;

      toDateValue = `${nextYear}-${nextMonthStr}-01`;
    }

    // convert the date to a timestamp value
    const fromDateTimestamp = fromDateValue
      ? new Date(fromDateValue).getTime() / 1000
      : null;
    const toDateTimestamp = toDateValue
      ? new Date(toDateValue).getTime() / 1000
      : null;

    let refineStr = '';

    if (fromDateTimestamp && toDateTimestamp) {
      refineStr = `${fromDateTimestamp} TO ${toDateTimestamp}`;
    } else if (fromDateTimestamp) {
      refineStr = `>= ${fromDateTimestamp}`;
    } else if (toDateTimestamp) {
      refineStr = `<= ${toDateTimestamp}`;
    }

    // add both from and to dates to the url so this syncs the state
    // with the url
    const url = new URL(window.location.href);
    if (refineStr === '') {
      url.searchParams.delete('date');
    } else {
      url.searchParams.set('date', refineStr);
    }

    window.history.pushState({}, '', url.toString());

    refine('');
    refresh();
  };

  return (
    <div className={wrapperClasses}>
      <button className="h2" onClick={() => setOpen(!open)} type="button">
        {title} {!alwaysOpen && <MemoChevron />}
      </button>

      <div className="case-notes__refinement-content">
        {hasSearchBox && (
          <div className="case-notes__refinement-search">
            <input
              type="text"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="submit"
              onClick={() => searchForItems(query)}
              aria-label="Search"
            >
              <MemoSearchIcon />
            </button>
          </div>
        )}

        {isDateFilter ? (
          <div className="case-notes__refinement-date">
            <label>
              From:
              <input
                type="text"
                placeholder="mm/yyyy"
                name="from"
                value={fromDate}
                onChange={(e) => {
                  const date = e.target.value;
                  // set the date if valid
                  setFromDate(date);
                }}
                onBlur={() => {
                  handleDateChange();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleDateChange();
                  }
                }}
              />
              {fromDate && (
                <button
                  type="button"
                  onClick={() => {
                    setFromDate('');
                    handleDateChange();
                  }}
                  className="case-notes__refinement-date-clear"
                  aria-label="Clear from date"
                >
                  <CloseIcon />
                </button>
              )}
            </label>

            <label>
              To:
              <input
                type="text"
                name="to"
                placeholder="mm/yyyy"
                value={toDate}
                onChange={(e) => {
                  const date = e.target.value;

                  // set the date if valid
                  setToDate(date);
                }}
                onBlur={() => {
                  handleDateChange();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleDateChange();
                  }
                }}
              />
              {toDate && (
                <button
                  type="button"
                  onClick={() => {
                    setToDate('');
                    handleDateChange();
                  }}
                  className="case-notes__refinement-date-clear"
                  aria-label="Clear from date"
                >
                  <CloseIcon />
                </button>
              )}
            </label>

            <button
              type="button"
              className="button button--primary button--small case-notes__refinement-date-submit"
              onClick={() => {
                handleDateChange();
              }}
            >
              Update
            </button>
          </div>
        ) : (
          <div className="scroll-wrapper">
            <ul>
              {filteredItems.map((item) => (
                <li key={item.value}>
                  <label>
                    <input
                      type="checkbox"
                      name={item.value}
                      checked={item.isRefined}
                      onChange={() => {
                        onRefine(item.value, !item.isRefined);
                        refine(item.value);
                        refresh();
                      }}
                    />

                    {item.isRefined && <MemoCheckboxIcon />}

                    <span className="case-notes__refinement-label">
                      {item.label} <span>{item.count}</span>
                    </span>
                  </label>
                </li>
              ))}

              {filteredItems.length === 0 && query && (
                <li>
                  <span>No matching facets found with search</span>
                </li>
              )}
            </ul>
            {!isShowingMore && canToggleShowMore && (
              <button
                onClick={toggleShowMore}
                className="button button--semantic"
                type="button"
              >
                <MemoPlusIcon />
                More options
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RefinementListWithSearch;
